import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Trans } from '@lingui/react';
import {
  UnitSystem,
  useGetInspectionPointQuery,
  useUpdateWearDataMutation,
  useWearDataQuery
} from 'providers/api';
import React from 'react';
import { useParams } from 'react-router-dom';
import PipeCrossSection, { RadarValues } from '../WearDataEntryPage/PipeCrossSection';
import WearDataForm from '../WearDataForm';
import { WearDataEntryPanelPageParams } from '../WearDataForm/constants';
import { WearDataInputs } from '../WearDataForm/WearDataSchema';
import PCSpool from '../MineModel/Spools/PCSpool';

interface WearDataUpdatePanelPageProps {
  unitSystemPreference: UnitSystem;
  successPath: string;
  spools: PCSpool[];
  inspectionPointId?: string;
  projectId?: string;
  wearDataId?: string;
}

const WearDataUpdatePanelPage = ({
  unitSystemPreference,
  successPath,
  spools,
  inspectionPointId: propInspectionPointId,
  projectId: propProjectId,
  wearDataId: propWearDataId,
}: WearDataUpdatePanelPageProps) => {
  const { entityId: urlProjectId, inspectionPointId: urlInspectionPointId, wearDataId: urlWearDataId } = useParams<WearDataEntryPanelPageParams>();
  const projectId = propProjectId || urlProjectId as string;
  const inspectionPointId = propInspectionPointId || urlInspectionPointId as string;
  const wearDataId = propWearDataId || urlWearDataId as string;
  const { data: wearData } = useWearDataQuery({ projectId, wearDataId });

  const { data: inspectionPointData } = useGetInspectionPointQuery(
    { projectId, inspectionPointId },
  );

  // The point measures a spool, and the spool owns the pipe whose cross-section is drawn.
  const spoolData = spools.find((spool) => spool.spoolId === inspectionPointData?.spoolId);
  const pipeData = spoolData?.pipe;

  const [currentWearData, setCurrentWearData] = React.useState<RadarValues | undefined>(wearData);

  const handleWearDataChange = (updatedData: WearDataInputs) => {
    const numericData: RadarValues = {
      position1: Number(updatedData.position1),
      position2: Number(updatedData.position2),
      position3: Number(updatedData.position3),
      position4: Number(updatedData.position4),
      position5: Number(updatedData.position5),
      position6: Number(updatedData.position6),
      position7: Number(updatedData.position7),
      position8: Number(updatedData.position8),
    };
    setCurrentWearData(numericData);
  };

  const updateWearData = useUpdateWearDataMutation(projectId as string);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        justifyContent: 'flex-start',
        alignItems: 'center',
        overflowY: 'auto',
        height: '100%',
        maxHeight: '100vh',
        minHeight: 0,
        padding: 2,
        boxSizing: 'border-box',
      }}
    >
      {currentWearData && pipeData && wearData ? (
        <PipeCrossSection
          wearDataNew={currentWearData}
          pipeData={pipeData}
        />
    ) : (
      <Typography variant="subtitle2"><Trans id="Pipe data or wear data is unavailable.">Pipe data or wear data is unavailable.</Trans></Typography>
    )}
      {
      wearData && (
        <WearDataForm
          wearData={wearData}
          inspectionPointId={inspectionPointId as string}
          unitSystemPreference={unitSystemPreference}
          successPath={successPath.replace(':inspectionPointId', inspectionPointId as string)}
          mutation={updateWearData}
          projectId={projectId as string}
          onChange={handleWearDataChange}
        />
      )
    }
    </Box>
  );
};

export default WearDataUpdatePanelPage;
